import { bestComboText } from "../components/Combo";
import { Coins } from "../components/Coins";
import { RankCard } from "../components/RankCard";
import type { Mode } from "../content/modes";
import { affordable } from "../game/items";
import type { BattleOutcome } from "../game/results";
import { lookFor, setModes } from "../game/sets";
import type { SaveState } from "../save/schema";
import "./Result.css";
import "./BattleResult.css";

interface Props {
  outcome: BattleOutcome;
  save: SaveState;
  /** The next ninja to recruit, if any, for the "Next ninja" button. */
  nextName: string | null;
  onAgain: () => void;
  onNext: () => void;
  onRoster: () => void;
  onShop: () => void;
  onHome: () => void;
  onOpenCard: (rankIndex: number, mode?: Mode) => void;
}

/** "8 right, 2 wrong. Hit for 120 of 380. Took 2 hits." */
export function battleScoreLine(o: BattleOutcome): string {
  const hits = o.hits === 0 ? "No hits taken!" : `Took ${o.hits} hit${o.hits === 1 ? "" : "s"}.`;
  const passed = o.passes ? ` (${o.passes} passed)` : "";
  return `${o.score} right, ${o.wrong} wrong${passed}. Hit for ${Math.min(o.damage, o.enemy.health)} of ${o.enemy.health}. ${hits}`;
}

export function BattleResult({
  outcome: o,
  save,
  nextName,
  onAgain,
  onNext,
  onRoster,
  onShop,
  onHome,
  onOpenCard,
}: Props) {
  const canShop = affordable(save.items, o.balance).length > 0;
  const used = o.itemsUsed.map((i) => i.name);
  // A win shows the beaten ninja's card (revealed if just recruited); a loss, your fighter.
  const shown = o.won ? o.enemy : o.fighter;
  const look = lookFor(save.rank, save.settings.look, shown.rankIndex, setModes(save.settings.ops));
  const kicker = o.recruited ? "New recruit" : o.won ? "Beaten" : "Your fighter";
  return (
    <section className="screen result battle-result">
      <div className="result-body">
        <div className="result-text">
          <p className={"banner display" + (o.won ? "" : " soft")}>{o.banner}</p>
          <p className="result-detail">{o.detail}</p>
          <p className="result-sub">{battleScoreLine(o)}</p>
          {bestComboText(o.bestCombo) && (
            <p className="result-combo">{bestComboText(o.bestCombo)}</p>
          )}
          {used.length > 0 && (
            <p className="result-sub used-line">
              Used{" "}
              {used.length > 1 ? `${used.slice(0, -1).join(", ")} and ${used.at(-1)}` : used[0]}.
            </p>
          )}
          <p className="coins-line">
            <Coins count={o.coins} delta className="coins-earned" />
            {o.coinBonus > 0 && (
              <span className="coins-bonus">+{o.coinBonus} of that from Lucky Charm</span>
            )}
            <span className="coins-total">
              You have <Coins count={o.balance} />
            </span>
          </p>
        </div>
        <RankCard
          rankIndex={shown.rankIndex}
          variant={look}
          kicker={kicker}
          onOpen={(i) => onOpenCard(i, look)}
          className={"result-card" + (o.recruited ? " reveal" : "")}
        />
        <div className="result-actions">
          {o.won && nextName ? (
            <button type="button" className="big display" onClick={onNext}>
              Next: {nextName}
            </button>
          ) : (
            <button type="button" className="big display" onClick={onAgain}>
              Battle again
            </button>
          )}
          <div className="row">
            {o.won && nextName && (
              <button type="button" className="ghost" onClick={onAgain}>
                Rematch
              </button>
            )}
            <button type="button" className="ghost" onClick={onRoster}>
              My ninjas
            </button>
            {canShop && (
              <button type="button" className="ghost" onClick={onShop}>
                Dojo Shop
              </button>
            )}
            <button type="button" className="ghost" onClick={onHome}>
              Back to the dojo
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
